// src/pages/Reporting.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";

const PRIMARY = "#1F2937";

// ── Download helper ───────────────────────────────────────────────────────────
async function downloadReport(url, method, body, filename) {
  const res = await fetch(url, {
    method,
    headers: method === "POST" ? { "Content-Type":"application/json" } : {},
    body:    method === "POST" ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const a    = document.createElement("a");
  a.href     = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontWeight:400, fontSize:13 }}>
      <div onClick={() => onChange(!value)} style={{
        width:36, height:20, borderRadius:99, background:value?PRIMARY:"#D1D5DB",
        position:"relative", transition:"background 0.15s", cursor:"pointer", flexShrink:0,
      }}>
        <div style={{
          width:16, height:16, borderRadius:"50%", background:"#fff",
          position:"absolute", top:2, left:value?18:2, transition:"left 0.15s",
          boxShadow:"0 1px 3px rgba(0,0,0,0.2)",
        }}/>
      </div>
      {label}
    </label>
  );
}

// ── Report card ───────────────────────────────────────────────────────────────
function ReportCard({ title, description, children }) {
  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontWeight:700, fontSize:14, color:"var(--gray-900)", marginBottom:4 }}>{title}</div>
        <div style={{ fontSize:13, color:"var(--gray-500)", lineHeight:1.5 }}>{description}</div>
      </div>
      {children}
    </div>
  );
}

// ── Download buttons ──────────────────────────────────────────────────────────
function DownloadButtons({ onPDF, onXLSX, loading, pdfOnly }) {
  return (
    <div style={{ display:"flex", gap:8, marginTop:14 }}>
      <button className="btn-primary" style={{ background:PRIMARY, fontSize:13, padding:"7px 16px" }}
        onClick={onPDF} disabled={loading}>
        {loading ? "Generating…" : "↓ PDF"}
      </button>
      {!pdfOnly && (
        <button className="btn-secondary" style={{ fontSize:13, padding:"7px 16px" }}
          onClick={onXLSX} disabled={loading}>
          {loading ? "Generating…" : "↓ XLSX"}
        </button>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Reporting() {
  const { user }     = useAuth();
  const [users,      setUsers]     = useState([]);
  const [courses,    setCourses]   = useState([]);
  const [m365Depts,  setM365Depts] = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [error,      setError]     = useState(null);

  // Per-report state
  const [selUser,    setSelUser]   = useState("");
  const [selCourse,  setSelCourse] = useState("");
  const [selDept,    setSelDept]   = useState("");
  const [selPolicy,  setSelPolicy] = useState("");
  const [policies,   setPolicies]  = useState([]);
  const [liveOnly,   setLiveOnly]  = useState(true);
  const [busy,       setBusy]      = useState(null); // which report is generating

  useEffect(() => {
    Promise.all([
      fetch("/api/users").then(r=>r.json()),
      fetch("/api/courses").then(r=>r.json()),
      fetch("/api/reports/departments").then(r=>r.json()),
      fetch("/api/policies").then(r=>r.json()),
    ]).then(([u, c, d, p]) => {
      setUsers(u.users || []);
      setCourses((c.courses || []).filter(x => x.status === "live"));
      setM365Depts(d.departments || []);
      setPolicies((p.policies || []).filter(x => x.requireAcceptance));
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function run(key, url, method, body, filename) {
    setBusy(key);
    setError(null);
    try {
      await downloadReport(url, method, body, filename);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }

  function userLabel(u) { return `${u.name||u.email} (${u.email})`; }
  function courseName(id) { return courses.find(c=>c.id===id)?.name || id; }
  function safeFile(str) { return str.replace(/[^a-z0-9]/gi,"-"); }

  if (loading) return <div className="page-loading"><span className="spinner" style={{width:28,height:28}}/></div>;

  const isBusy = (k) => busy === k;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Reporting</div>
          <div className="page-subtitle">Export training data and compliance summaries</div>
        </div>
      </div>

      {error && (
        <div style={{ background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:"var(--radius)", padding:"12px 16px", marginBottom:16, fontSize:13, color:"#DC2626" }}>
          {error}
        </div>
      )}

      {/* Options */}
      <div className="card" style={{ marginBottom:20, padding:"14px 20px" }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:"var(--gray-700)" }}>Global Options</div>
        <Toggle value={liveOnly} onChange={setLiveOnly} label="Live courses only (exclude retired / draft)"/>
      </div>

      {/* Section: Data Reports */}
      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--gray-400)", marginBottom:12 }}>
        Data Reports — PDF &amp; XLSX
      </div>

      {/* User summary */}
      <ReportCard
        title="User Training Summary"
        description={user?.role === "supervisor"
          ? "Completion status for a specific user, scoped to courses you manage."
          : "All courses and completion status for a specific user."}
      >
        {user?.role === "supervisor" && (
          <div style={{ fontSize:12, color:"#92400E", background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:6, padding:"6px 10px", marginBottom:8 }}>
            ⚠ Report will only include courses within your management scope.
          </div>
        )}
        <div className="form-group" style={{ margin:0 }}>
          <label>Select User</label>
          <select value={selUser} onChange={e=>setSelUser(e.target.value)}>
            <option value="">— Choose user —</option>
            {users.filter(u=>!u.deletedAt).map(u=>(
              <option key={u.email} value={u.email}>{userLabel(u)}</option>
            ))}
          </select>
        </div>
        <DownloadButtons
          loading={isBusy("user-pdf") || isBusy("user-xlsx")}
          onPDF={()=>run("user-pdf","/api/reports/user/pdf","POST",{ email:selUser, liveOnly },`User-${safeFile(selUser)}.pdf`)}
          onXLSX={()=>run("user-xlsx","/api/reports/user/xlsx","POST",{ email:selUser, liveOnly },`User-${safeFile(selUser)}.xlsx`)}
        />
      </ReportCard>

      {/* Course summary */}
      <ReportCard
        title="Course Completion Summary"
        description="All users and their completion status for a specific course."
      >
        <div className="form-group" style={{ margin:0 }}>
          <label>Select Course</label>
          <select value={selCourse} onChange={e=>setSelCourse(e.target.value)}>
            <option value="">— Choose course —</option>
            {courses.map(c=>(
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <DownloadButtons
          loading={isBusy("course-pdf") || isBusy("course-xlsx")}
          onPDF={()=>run("course-pdf","/api/reports/course/pdf","POST",{ courseId:selCourse, liveOnly },`Course-${safeFile(courseName(selCourse))}.pdf`)}
          onXLSX={()=>run("course-xlsx","/api/reports/course/xlsx","POST",{ courseId:selCourse, liveOnly },`Course-${safeFile(courseName(selCourse))}.xlsx`)}
        />
      </ReportCard>

      {/* Department summary */}
      <ReportCard
        title="Department Training Summary"
        description={user?.role === "supervisor"
          ? "All users in a department, scoped to courses you manage."
          : "All users and courses for a specific M365 department."}
      >
        {user?.role === "supervisor" && (
          <div style={{ fontSize:12, color:"#92400E", background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:6, padding:"6px 10px", marginBottom:8 }}>
            ⚠ Report will only include courses within your management scope.
          </div>
        )}
        <div className="form-group" style={{ margin:0 }}>
          <label>Select Department</label>
          <select value={selDept} onChange={e=>setSelDept(e.target.value)}>
            <option value="">— Choose department —</option>
            {m365Depts.map(d=>(
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <DownloadButtons
          loading={isBusy("dept-pdf") || isBusy("dept-xlsx")}
          onPDF={()=>run("dept-pdf","/api/reports/department/pdf","POST",{ department:selDept, liveOnly },`Dept-${safeFile(selDept)}.pdf`)}
          onXLSX={()=>run("dept-xlsx","/api/reports/department/xlsx","POST",{ department:selDept, liveOnly },`Dept-${safeFile(selDept)}.xlsx`)}
        />
      </ReportCard>

      {/* Section: Compliance Reports */}
      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--gray-400)", marginBottom:12, marginTop:8 }}>
        Compliance Summaries — PDF Only
      </div>

      {/* Course compliance */}
      <ReportCard
        title="Course Compliance Summary"
        description="Current compliance snapshot for all users of a specific course — who has completed, who hasn't, who's overdue."
      >
        <div className="form-group" style={{ margin:0 }}>
          <label>Select Course</label>
          <select value={selCourse} onChange={e=>setSelCourse(e.target.value)}>
            <option value="">— Choose course —</option>
            {courses.map(c=>(
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <DownloadButtons pdfOnly
          loading={isBusy("comp-course")}
          onPDF={()=>run("comp-course","/api/reports/compliance","POST",{ type:"course", id:selCourse },`Compliance-Course-${safeFile(courseName(selCourse))}.pdf`)}
        />
      </ReportCard>

      {/* User compliance */}
      <ReportCard
        title="User Compliance Summary"
        description="Current compliance snapshot for a specific user across all mandatory courses."
      >
        <div className="form-group" style={{ margin:0 }}>
          <label>Select User</label>
          <select value={selUser} onChange={e=>setSelUser(e.target.value)}>
            <option value="">— Choose user —</option>
            {users.filter(u=>!u.deletedAt).map(u=>(
              <option key={u.email} value={u.email}>{userLabel(u)}</option>
            ))}
          </select>
        </div>
        <DownloadButtons pdfOnly
          loading={isBusy("comp-user")}
          onPDF={()=>run("comp-user","/api/reports/compliance","POST",{ type:"user", id:selUser },`Compliance-User-${safeFile(selUser)}.pdf`)}
        />
      </ReportCard>

      {/* Department compliance */}
      <ReportCard
        title="Department Compliance Summary"
        description="Current compliance snapshot for all users in a specific department."
      >
        <div className="form-group" style={{ margin:0 }}>
          <label>Select Department</label>
          <select value={selDept} onChange={e=>setSelDept(e.target.value)}>
            <option value="">— Choose department —</option>
            {m365Depts.map(d=>(
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <DownloadButtons pdfOnly
          loading={isBusy("comp-dept")}
          onPDF={()=>run("comp-dept","/api/reports/compliance","POST",{ type:"department", department:selDept },`Compliance-Dept-${safeFile(selDept)}.pdf`)}
        />
      </ReportCard>

      {/* Policy acceptance */}
      {policies.length > 0 && <>
        <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--gray-400)", marginBottom:12, marginTop:24 }}>
          Policy Acceptance
        </div>
        <ReportCard
          title="Policy Acceptance Report"
          description="See which users have accepted a specific policy and when."
        >
          <div className="form-group" style={{ margin:0 }}>
            <label>Select Policy</label>
            <select value={selPolicy} onChange={e=>setSelPolicy(e.target.value)}>
              <option value="">— Choose policy —</option>
              {policies.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <DownloadButtons
            loading={isBusy("policy-acc")}
            onPDF={()=>run("policy-acc","/api/reports/policy-acceptance","POST",{ policyId:selPolicy },`Policy-Acceptance-${safeFile(selPolicy)}.pdf`)}
            onXLSX={()=>run("policy-acc-xl","/api/reports/policy-acceptance","POST",{ policyId:selPolicy, format:"xlsx" },`Policy-Acceptance-${safeFile(selPolicy)}.xlsx`)}
          />
        </ReportCard>

        <ReportCard
          title="Policy Activity by User"
          description="Full audit log for a specific user — every policy they have viewed or accepted, with timestamps."
        >
          <div className="form-group" style={{ margin:0 }}>
            <label>Select User</label>
            <select value={selUser} onChange={e=>setSelUser(e.target.value)}>
              <option value="">— Choose user —</option>
              {users.filter(u=>u.active&&!u.deletedAt).map(u=>(
                <option key={u.email} value={u.email}>{u.name||u.email} ({u.email})</option>
              ))}
            </select>
          </div>
          <DownloadButtons
            loading={isBusy("policy-log")}
            onPDF={()=>run("policy-log","/api/reports/policy-log","POST",{ email:selUser },`Policy-Log-${safeFile(selUser)}.pdf`)}
            onXLSX={()=>run("policy-log-xl","/api/reports/policy-log","POST",{ email:selUser, format:"xlsx" },`Policy-Log-${safeFile(selUser)}.xlsx`)}
          />
        </ReportCard>
      </>}
    </div>
  );
}

